import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import WordSearchPuzzleConfig from '../interfaces/word-search-puzzle.config';
import WordSearchPuzzlePoint from '../interfaces/word-search-puzzle-point';
import WordSearchPuzzleWord from '../interfaces/word-search-puzzle-word';
import { PuzzleZoomService } from '../../shared-module/puzzle-zoom/puzzle-zoom.service';
import { PuzzleGameState } from '../../store/puzzle-game.state';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import { Store } from '@ngxs/store';
import { SetPuzzleID } from '../../store/puzzle-id.actions';
import { InitWordSearchPuzzle, PersistWordSearchState } from '../store/word-search-puzzle.actions';
import { WordSearchPuzzleState } from '../store/word-search-puzzle.state';
import { Button } from '../../shared-module/buttons/button.def';
import { Subscription } from 'rxjs';
import { IncMoveCounter, SetFinished, SetGameProgress } from '../../store/puzzle-game.actions';
import {
  PUZZLE_WIN_DIALOG_BUTTONS,
  PuzzleWinDialogService,
} from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { WordSearchPuzzleWinScreenComponent } from '../win-screen/word-search-puzzle-win-screen.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { PuzzleHelpDialogService } from '../../shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import { WordSearchHelpComponent } from '../help/word-search-help.component';
import { WordSearchPuzzleModel } from '../store/word-search-puzzle.model';
import { PuzzleFrameworkEvents } from '../../puzzle-framework-module/puzzle-framework-events.enum';
import { ResizeObserver } from '@juggle/resize-observer';
import { PuzzleTypeIdEnum } from '../../puzzle-framework-module/puzzle-type-id.enum';
import { WordSearchPuzzleWordListComponent } from 'projects/tools/src/lib/word-search-puzzle-module/word-list/word-search-puzzle-word-list.component';
import { PuzzleSettingsModalService } from '../../shared-module/puzzle-settings-modal/puzzle-settings-modal.service';

export enum WordSearchPuzzleDirections {
  NORTH,
  NORTH_EAST,
  EAST,
  SOUTH_EAST,
  SOUTH,
  SOUTH_WEST,
  WEST,
  NORTH_WEST,
}

const WORD_LIST_MIN_HEIGHT_PX = 100;
const WORD_LIST_MIN_WIDTH_PX = 250;

@Component({
  selector: 'lib-app-word-search-puzzle-board',
  templateUrl: './word-search-puzzle-board.component.html',
  styleUrls: ['./word-search-puzzle-board.component.scss'],
  standalone: true,
  imports: [CommonModule, WordSearchPuzzleWordListComponent],
})
export class WordSearchPuzzleBoardComponent implements OnInit, AfterViewInit {
  @ViewChild('selectCanvas')
  public selectCanvas: ElementRef<HTMLCanvasElement>;
  public selectContext: CanvasRenderingContext2D;

  @ViewChild('backgroundCanvas')
  public backgroundCanvas: ElementRef<HTMLCanvasElement>;
  public backgroundContext: CanvasRenderingContext2D;

  @ViewChild('board', { static: true }) boardRef: ElementRef;
  @ViewChild('wordList', { static: true }) wordListRef: ElementRef;

  public grid: string[][];

  public rows: number;
  public cols: number;

  public heightPX: number;
  public widthPX: number;

  public fontSize: number;

  public scaleValue = 0;
  public bottomWordList = false;

  public horizontalWordListHeight = '' + WORD_LIST_MIN_HEIGHT_PX + 'px';
  public verticalWordListWidth = '' + WORD_LIST_MIN_WIDTH_PX + 'px';

  public solutionFields: number[][] = [];
  public showSolution = false;
  public hasSolutionWord = false;

  public wordList: WordSearchPuzzleWord[] = [];
  public foundWords: WordSearchPuzzleWord[] = [];

  public initialized = false;

  private solutionWord: string;
  private solutionOriginal: string;

  private selectStartOffset: { left: number; top: number };
  private select: boolean;
  private selectStart: WordSearchPuzzlePoint;
  private selectStartMouseEvent: MouseEvent;
  private selectStartTouchEvent: TouchEvent;

  public selectedFields: boolean[][];
  private selectEnd: WordSearchPuzzlePoint;
  private selectDiagonal = false;
  private selectDirection: WordSearchPuzzleDirections;

  private puzzleConfig: WordSearchPuzzleConfig;

  private puzzleID: string;

  private buttonBarSubscription: Subscription;
  private winDialogSubscription: Subscription;
  private helpDialogSubscription: Subscription;
  private scaleTimeout: number;
  private puzzleTypeID: PuzzleTypeIdEnum;
  disableDraw = false;

  constructor(
    private el: ElementRef,
    private puzzleFramework: PuzzleFrameworkService,
    private zoomService: PuzzleZoomService,
    private store: Store,
    private winDialogService: PuzzleWinDialogService,
    private breakpointObserver: BreakpointObserver,
    private helpDialogService: PuzzleHelpDialogService,
    private settingsModalService: PuzzleSettingsModalService
  ) {
    this.winDialogSubscription = this.winDialogService.winDialogRegistered.subscribe(async () => {
      this.winDialogService.showButtons([
        PUZZLE_WIN_DIALOG_BUTTONS.RESTART_GAME,
        PUZZLE_WIN_DIALOG_BUTTONS.BACK_TO_GAME,
      ]);

      const data = {
        hasSolutionWord: this.hasSolutionWord,
        solutionWord: this.solutionWord,
        solutionOriginal: this.solutionOriginal,
      };

      this.winDialogService.injectComponent(WordSearchPuzzleWinScreenComponent, data);
      if (this.hasSolutionWord) {
        this.winDialogService.addSolutionWord(this.solutionWord);
      }

      this.disableDraw = true;
    });

    this.puzzleFramework.puzzleConfigLoaded$.subscribe((id) => {
      this.puzzleTypeID = id;
    });

    this.helpDialogSubscription = this.helpDialogService.helpDialogRegistered.subscribe(async () => {
      const translationKey = this.puzzleFramework.getTranslationKey() || 'WORD_SEARCH';
      this.helpDialogService.injectHelpComponent(WordSearchHelpComponent, { translationKey });
    });

    this.buttonBarSubscription = this.puzzleFramework.buttonPressed.subscribe((button) => {
      if (button === Button.Reset) {
        this.showSolution = false;
        this.disableDraw = false;
        this.puzzleFramework.initPuzzleGameState(
          this.puzzleID,
          PuzzleGameTypesEnum.WordSearchPuzzle,
          0,
          this.solutionWord,
          this.solutionOriginal
        );
        this.initWordSearchRedux();
      }
    });

    this.puzzleFramework.puzzleEvents.subscribe((event) => {
      if (event === PuzzleFrameworkEvents.SOLVE_CURRENT_PUZZLE) {
        this._winGame();
      }
    });
  }

  private _winGame() {
    this.store.dispatch(new PersistWordSearchState({ puzzleID: this.puzzleID, words: this.wordList }));
  }

  ngOnInit(): void {
    const puzzleConfig = this.puzzleFramework.puzzleConfig.puzzle as WordSearchPuzzleConfig;
    this.puzzleConfig = puzzleConfig;

    const puzzleID = puzzleConfig._id;

    this.puzzleID = puzzleID;

    this.store.dispatch(new SetPuzzleID({ puzzleID }));

    this.store.select(PuzzleGameState.getByID(puzzleID)).subscribe((puzzleGame) => {
      if (puzzleGame === null) {
        this.puzzleFramework.initPuzzleGameState(
          puzzleID,
          PuzzleGameTypesEnum.WordSearchPuzzle,
          0,
          this.solutionWord,
          this.solutionOriginal
        );
        return;
      }
    });

    this.wordList = puzzleConfig.game.word.map((word) => ({
      start: { column: parseInt(word._startx, 10), row: parseInt(word._starty, 10) },
      end: { column: parseInt(word._endx, 10), row: parseInt(word._endy, 10) },
      text: word.__text,
    }));

    const rowCount = parseInt(puzzleConfig._rowCount, 10);
    const colCount = parseInt(puzzleConfig._colCount, 10);

    this.rows = rowCount;
    this.cols = colCount;

    this.heightPX = this.rows * 50;
    this.widthPX = this.cols * 50;

    this.selectedFields = [];
    this.grid = [];
    for (let row = 0; row < rowCount; row++) {
      this.grid[row] = [];
      this.solutionFields[row] = [];
      this.selectedFields[row] = [];
      for (let col = 0; col < colCount; col++) {
        const letter = puzzleConfig.game.row[row].__text[col];
        this.grid[row][col] = letter === '#' ? '' : letter;
        this.selectedFields[row][col] = false;
        this.solutionFields[row][col] = -1;
      }
    }

    if (puzzleConfig.game.hasOwnProperty('solution') && puzzleConfig.game.solution.hasOwnProperty('pos')) {
      this.winDialogService.solutionDialogDelay = 2000; // Create a delay for solution dialog to show the solution word
      puzzleConfig.game.solution.pos.map((field, index) => {
        const x = parseInt(field._x, 10);
        const y = parseInt(field._y, 10);
        // this.wordList[x * this.cols + y].solutionIndex = index;
        this.solutionFields[y][x] = index;
      });
      this.hasSolutionWord = true;
      this.solutionWord = puzzleConfig.game.solution._word;
      this.solutionOriginal = puzzleConfig.game.solution._original ? puzzleConfig.game.solution._original : undefined;
    } else {
      this.winDialogService.solutionDialogDelay = 0; // Remove delay
    }

    const observer = new ResizeObserver((entries) => {
      this.scaleBoard();
    });
    observer.observe(this.el.nativeElement);
  }

  ngAfterViewInit(): void {
    this.selectContext = this.selectCanvas.nativeElement.getContext('2d');
    this.backgroundContext = this.backgroundCanvas.nativeElement.getContext('2d');
    this.backgroundContext.globalAlpha = parseFloat(
      window.getComputedStyle(document.body).getPropertyValue('--word-search-select-opacity')
    );
    // this.drawRoundRect(30, 60, 50, 600, 25, '#FF0000', '#000000');
    // this.drawRoundRect(60, 30, 20, 600, 10, '#FF0000', '#000000');
    const puzzleID = this.puzzleID;

    this.store.select(WordSearchPuzzleState.getByID(puzzleID)).subscribe((wordSearchGame: WordSearchPuzzleModel) => {
      if (wordSearchGame === null) {
        this.initWordSearchRedux(puzzleID);
        return;
      }

      window.setTimeout(() => {
        this.foundWords = wordSearchGame.words;
        this.drawFoundWords();
        this.updateProgress(); // Update game progress everytime the game is loaded from redux
        this.checkForWin();
      }, 1);
    });

    // calculate scaling for print mediaquery
    const printScale = Math.min(800 / this.heightPX, 610 / this.widthPX);
    this.el.nativeElement.style.setProperty('--word-search-print-scale', printScale);
    this.el.nativeElement.style.setProperty('--word-search-print-width', printScale * this.widthPX);
    this.el.nativeElement.style.setProperty('--word-search-print-height', printScale * this.heightPX);

    window.setTimeout(() => {
      this.scaleBoard();
    }, 1);
  }

  private updateProgress() {
    this.store.dispatch(
      new SetGameProgress({
        puzzleID: this.puzzleID,
        progress: this.foundWords.length / this.wordList.length,
      })
    );
  }

  getClientRect() {
    return this.boardRef.nativeElement.getBoundingClientRect();
  }

  imgWidth() {
    return this.cols * 50;
  }

  imgHeight() {
    return this.rows * 50;
  }

  // @HostListener('window:resize')
  scaleBoard() {
    if (document.body.clientWidth === 0) {
      // since the ionic update, inactive browser tabs dont have a body width
      // as long as the tab is active, postpone the zoom operation
      if (this.scaleTimeout) {
        clearTimeout(this.scaleTimeout);
        this.scaleTimeout = undefined;
      }
      this.scaleTimeout = window.setTimeout(() => {
        this.scaleBoard();
      }, 100);
      return;
    }

    const computedStyle = getComputedStyle(document.documentElement);

    const minFontSize = parseInt(
      computedStyle.getPropertyValue('--word-search-list-font-min-size').replace('px', ''),
      10
    );

    const boardFontSize = parseInt(computedStyle.getPropertyValue('--word-search-font-size').replace('px', ''), 10);

    const availableHeight = this.el.nativeElement.offsetHeight;
    const availableWidth = this.el.nativeElement.offsetWidth;

    const boardMargins = Math.min(availableWidth, availableHeight) < 600 ? 20 : 60;

    const sideWordListWidth = WORD_LIST_MIN_WIDTH_PX;
    const bottomWordListHeight = WORD_LIST_MIN_HEIGHT_PX;

    const targetBoardHeight = availableHeight - boardMargins;
    const targetBoardWidth = availableWidth - boardMargins;

    const unscaledBoardHeight = this.rows * 50;
    const unscaledBoardWidth = this.cols * 50;

    const zoomWithSideWordList = Math.min(
      (availableHeight - boardMargins) / unscaledBoardHeight,
      (availableWidth - boardMargins - sideWordListWidth) / unscaledBoardWidth
    );
    const zoomWithBottomWordList = Math.min(
      (availableHeight - boardMargins - bottomWordListHeight) / unscaledBoardHeight,
      (availableWidth - boardMargins) / unscaledBoardWidth
    );

    const finalScale = this.puzzleFramework.puzzleFrameworkConfig.wordSearchBottomWordListOnly
      ? zoomWithBottomWordList
      : Math.max(zoomWithBottomWordList, zoomWithSideWordList);

    switch (finalScale) {
      case zoomWithSideWordList:
        this.bottomWordList = false;
        this.verticalWordListWidth =
          '' + Math.max(WORD_LIST_MIN_WIDTH_PX, availableWidth - finalScale * unscaledBoardWidth - boardMargins) + 'px';
        break;
      case zoomWithBottomWordList:
        this.bottomWordList = true;
        this.horizontalWordListHeight =
          '' +
          Math.max(WORD_LIST_MIN_HEIGHT_PX, availableHeight - finalScale * unscaledBoardHeight - boardMargins) +
          'px';
        break;
    }

    // console.log(this.horizontalWordListHeight, this.verticalWordListWidth);
    this.scaleValue = finalScale;

    this.fontSize = Math.min(minFontSize, boardFontSize * finalScale);

    this.initialized = true;
  }

  private drawFoundWords() {
    this.clearCanvas(this.backgroundCanvas);
    this.foundWords.map((word) => {
      this.drawWordOnCanvas(this.backgroundContext, this.getWordDirection(word), word.end, word.start);
    });
  }

  private getWordDirection(word: WordSearchPuzzleWord): WordSearchPuzzleDirections {
    const offsetX = word.start.column - word.end.column;
    const offsetY = word.start.row - word.end.row;

    const angle = Math.atan2(offsetX, offsetY);
    const degrees = (360 + Math.round((-180 * angle) / Math.PI)) % 360;

    let direction: WordSearchPuzzleDirections;

    switch (true) {
      case degrees >= 337.5:
      case degrees < 22.5:
        direction = WordSearchPuzzleDirections.NORTH;
        break;
      case degrees < 67.5:
        direction = WordSearchPuzzleDirections.NORTH_EAST;
        break;
      case degrees < 112.5:
        direction = WordSearchPuzzleDirections.EAST;
        break;
      case degrees < 157.5:
        direction = WordSearchPuzzleDirections.SOUTH_EAST;
        break;
      case degrees < 202.5:
        direction = WordSearchPuzzleDirections.SOUTH;
        break;
      case degrees < 247.5:
        direction = WordSearchPuzzleDirections.SOUTH_WEST;
        break;
      case degrees < 292.5:
        direction = WordSearchPuzzleDirections.WEST;
        break;
      case degrees < 337.5:
        direction = WordSearchPuzzleDirections.NORTH_WEST;
        break;
    }

    return direction;
  }

  private initWordSearchRedux(puzzleID: string = this.puzzleID, words: WordSearchPuzzleWord[] = []) {
    this.store.dispatch(new InitWordSearchPuzzle({ puzzleID, words }));
  }

  private drawWordOnCanvas(
    context: CanvasRenderingContext2D = this.selectContext,
    selectDirection: WordSearchPuzzleDirections = this.selectDirection,
    wordEnd: WordSearchPuzzlePoint = this.selectEnd,
    wordStart: WordSearchPuzzlePoint = this.selectStart
  ) {
    const straightCellLength = 50;
    const diagonalCellLength = 50 * Math.sqrt(2);

    let rotation;
    const x = wordStart.column * straightCellLength;
    const y = wordStart.row * straightCellLength;
    let length;

    const radius = straightCellLength / 2;

    switch (selectDirection) {
      case WordSearchPuzzleDirections.NORTH:
        length = (wordStart.row - wordEnd.row + 1) * straightCellLength;
        rotation = -0.5;
        break;
      case WordSearchPuzzleDirections.NORTH_EAST:
        length = (wordStart.row - wordEnd.row + 1) * diagonalCellLength - radius;
        rotation = -0.25;
        break;
      case WordSearchPuzzleDirections.EAST:
        length = (wordEnd.column - wordStart.column + 1) * straightCellLength;
        rotation = 0;
        break;
      case WordSearchPuzzleDirections.SOUTH_EAST:
        length = (wordEnd.column - wordStart.column + 1) * diagonalCellLength - radius;
        rotation = 0.25;
        break;
      case WordSearchPuzzleDirections.SOUTH:
        length = (wordEnd.row - wordStart.row + 1) * straightCellLength;
        rotation = 0.5;
        break;
      case WordSearchPuzzleDirections.SOUTH_WEST:
        length = (wordEnd.row - wordStart.row + 1) * diagonalCellLength - radius;
        rotation = 0.75;
        break;
      case WordSearchPuzzleDirections.WEST:
        length = (wordStart.column - wordEnd.column + 1) * straightCellLength;
        rotation = 1;
        break;
      case WordSearchPuzzleDirections.NORTH_WEST:
        length = (wordStart.column - wordEnd.column + 1) * diagonalCellLength - radius;
        rotation = 1.25;
        break;
    }

    // rotate around center - find mid-point using lerp
    const midPoint = {
      x: wordStart.column * straightCellLength + radius,
      y: wordStart.row * straightCellLength + radius,
    };

    // translate to midpoint
    context.translate(midPoint.x, midPoint.y);

    // rotate some angle (radians)
    context.rotate(rotation * Math.PI); // = 45°

    context.translate(-midPoint.x, -midPoint.y);

    context.fillStyle = window.getComputedStyle(document.body).getPropertyValue('--word-search-select-color');

    const verticalSpace = 8;
    const horizontalSpace = 8;

    // Draw
    context.beginPath();
    context.moveTo(x + radius, y + verticalSpace);
    context.lineTo(x + length - radius, y + verticalSpace);

    context.quadraticCurveTo(x + length - horizontalSpace, y + verticalSpace, x + length - horizontalSpace, y + radius);

    context.quadraticCurveTo(
      x + length - horizontalSpace,
      y + straightCellLength - verticalSpace,
      x + length - radius,
      y + straightCellLength - verticalSpace
    );

    context.lineTo(x + radius, y + straightCellLength - verticalSpace);

    context.quadraticCurveTo(
      x + horizontalSpace,
      y + straightCellLength - verticalSpace,
      x + horizontalSpace,
      y + radius
    );

    context.quadraticCurveTo(x + horizontalSpace, y + verticalSpace, x + radius, y + verticalSpace);

    context.fill();

    // context.resetTransform();
    // ie save transformation reset:
    context.translate(midPoint.x, midPoint.y);
    context.rotate(rotation * Math.PI * -1); // = 45°
    context.translate(-midPoint.x, -midPoint.y);
  }

  cellIsSelected(column: number, row: number) {
    return (
      (this.selectStart && column === this.selectStart.column && row === this.selectStart.row) ||
      (this.selectEnd && column === this.selectEnd.column && row === this.selectEnd.row)
    );
  }

  cellTouchDown(event: TouchEvent, column: number, row: number) {
    if (this.disableDraw) {
      return;
    }

    // console.log('touchdown', event);
    event.preventDefault();
    this.select = true;
    this.selectStart = { column, row };
    this.selectStartTouchEvent = event;
    this.selectStartOffset = this.getSelectStartOffset(
      event.target as HTMLElement,
      event.touches[0].pageX,
      event.touches[0].pageY
    );
  }

  cellMouseDown(event: MouseEvent, column: number, row: number) {
    // console.log('mousedown');
    if (event.shiftKey || this.disableDraw) {
      return;
    }
    this.select = true;
    event.preventDefault();
    this.selectStart = { column, row };
    this.selectStartMouseEvent = event;
    this.selectStartOffset = this.getSelectStartOffset(event.target as HTMLElement, event.pageX, event.pageY);
  }

  private getSelectStartOffset(element: HTMLElement, pageX = 0, pageY = 0): { left: number; top: number } {
    const boundingBox = element.getBoundingClientRect();

    const offsetX = pageX - boundingBox.left - boundingBox.width / 2;
    const offsetY = pageY - boundingBox.top - boundingBox.height / 2;

    return { left: offsetX, top: offsetY };
  }

  @HostListener('window:mousemove', ['$event'])
  windowMouseMove(event: MouseEvent) {
    if (!this.select) {
      return;
    }

    // get move offset from center point of element
    const offsetX = this.selectStartMouseEvent.pageX - this.selectStartOffset.left - event.pageX;
    const offsetY = this.selectStartMouseEvent.pageY - this.selectStartOffset.top - event.pageY;

    this.canvasMove(offsetX, offsetY);

    event.preventDefault();
  }

  @HostListener('window:touchmove', ['$event'])
  windowTouchMove(event: TouchEvent) {
    if (!this.select) {
      return;
    }

    // get move offset from center point of element
    const offsetX = this.selectStartTouchEvent.touches[0].pageX - this.selectStartOffset.left - event.touches[0].pageX;
    const offsetY = this.selectStartTouchEvent.touches[0].pageY - this.selectStartOffset.top - event.touches[0].pageY;

    this.canvasMove(offsetX, offsetY);

    event.preventDefault();
  }

  private canvasMove(offsetX: number, offsetY: number) {
    const angle = Math.atan2(offsetX, offsetY);
    const degrees = (360 + Math.round((-180 * angle) / Math.PI)) % 360;

    let column = this.selectStart.column;
    let row = this.selectStart.row;

    const collumnDistance = Math.floor(Math.abs(offsetX) / 50 / this.scaleValue);
    const rowDistance = Math.floor(Math.abs(offsetY) / 50 / this.scaleValue);

    const magnitude = Math.max(collumnDistance, rowDistance);
    const diagonalMagnitude = Math.ceil((collumnDistance + rowDistance) / 2);

    let step = 0;
    let stepRow = 0;
    let stepCol = 0;
    let totalSteps = 0;

    switch (true) {
      case degrees >= 337.5:
      case degrees < 22.5:
        // row -= magnitude;
        this.selectDirection = WordSearchPuzzleDirections.NORTH;
        stepRow = -1;
        totalSteps = magnitude;
        break;
      case degrees < 67.5:
        // row -= diagonalMagnitude;
        // column += diagonalMagnitude;
        this.selectDirection = WordSearchPuzzleDirections.NORTH_EAST;
        stepRow = -1;
        stepCol = 1;
        totalSteps = diagonalMagnitude;
        break;
      case degrees < 112.5:
        // column += magnitude;
        this.selectDirection = WordSearchPuzzleDirections.EAST;
        stepCol = 1;
        totalSteps = magnitude;
        break;
      case degrees < 157.5:
        // column += diagonalMagnitude;
        // row += diagonalMagnitude;
        this.selectDirection = WordSearchPuzzleDirections.SOUTH_EAST;
        stepRow = 1;
        stepCol = 1;
        totalSteps = diagonalMagnitude;
        break;
      case degrees < 202.5:
        // row += magnitude;
        this.selectDirection = WordSearchPuzzleDirections.SOUTH;
        stepRow = 1;
        totalSteps = magnitude;
        break;
      case degrees < 247.5:
        // row += diagonalMagnitude;
        // column -= diagonalMagnitude;
        this.selectDirection = WordSearchPuzzleDirections.SOUTH_WEST;
        stepRow = 1;
        stepCol = -1;
        totalSteps = diagonalMagnitude;
        break;
      case degrees < 292.5:
        // column -= magnitude;
        this.selectDirection = WordSearchPuzzleDirections.WEST;
        stepCol = -1;
        totalSteps = magnitude;
        break;
      case degrees < 337.5:
        // row -= diagonalMagnitude;
        // column -= diagonalMagnitude;
        this.selectDirection = WordSearchPuzzleDirections.NORTH_WEST;
        stepRow = -1;
        stepCol = -1;
        totalSteps = diagonalMagnitude;
        break;
    }

    this.selectedFields = this.selectedFields.map((selectedRow) => selectedRow.map(() => false));
    this.selectedFields[this.selectStart.row][this.selectStart.column] = true;

    let foundBorder = false;
    while (foundBorder === false && step < totalSteps) {
      const newRow = row + stepRow;
      const newCol = column + stepCol;

      if (!this.grid[newRow] || !this.grid[newRow][newCol] || this.grid[newRow][newCol].length === 0) {
        foundBorder = true;
        break;
      }

      row = newRow;
      column = newCol;

      this.selectedFields[row][column] = true;

      step++;
    }

    const selectEnd = { row, column };
    if (!this.selectEnd || this.selectEnd.column !== selectEnd.column || this.selectEnd.row !== selectEnd.row) {
      this.selectEnd = selectEnd;
      this.clearCanvas();
      if (step > 0) {
        this.drawWordOnCanvas();
      }
    }
  }

  @HostListener('window:touchend', ['$event'])
  @HostListener('window:mouseup', ['$event'])
  windowMouseup(event: MouseEvent | TouchEvent) {
    // console.log('mouseup');
    this.select = false;
    if (
      !this.selectStart ||
      !this.selectEnd ||
      (this.selectStart.column === this.selectEnd.column && this.selectStart.row === this.selectEnd.row)
    ) {
      return;
    }

    this.store.dispatch(new IncMoveCounter({ puzzleID: this.puzzleID }));

    this.selectedFields = this.selectedFields.map((selectedRow) => selectedRow.map(() => false));

    this.wordList
      .filter((word) => this.checkIfWordIsSelected(word))
      .map((word) => {
        // this.drawWordOnCanvas(this.backgroundContext);
        const words = [...this.foundWords, ...[word]];
        this.store.dispatch(new PersistWordSearchState({ puzzleID: this.puzzleID, words }));
      });

    this.selectStart = undefined;
    this.selectEnd = undefined;
    this.selectStartMouseEvent = undefined;
    this.selectStartTouchEvent = undefined;
    // evtl timeout?
    window.setTimeout(() => this.clearCanvas(), 10);
    event.preventDefault();
  }

  private checkForWin() {
    const found = this.foundWords.map((w) => w.text);
    const left = this.wordList.map((w) => w.text).filter((s) => found.indexOf(s) === -1);

    if (left.length === 0) {
      this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
      this.showSolution = true;
    }
  }

  // @HostListener('window:touchend', ['$event'])
  // windowTouchEnd(event: TouchEvent) {
  //   console.log('touchend');
  //   this.select = false;
  //   if (!this.selectStart || !this.selectEnd) {
  //     return;
  //   }
  //
  //   this.wordList
  //     .filter(word => {
  //       return this.checkIfWordIsSelected(word);
  //     })
  //     .map(word => {
  //       // this.drawWordOnCanvas(this.backgroundContext);
  //       this.foundWords.push(word);
  //       this.store.dispatch(new PersistWordSearchState({ puzzleID: this.puzzleID, words: this.foundWords }));
  //     });
  //
  //   this.selectEnd = undefined;
  //   this.selectStart = undefined;
  //   this.selectStartTouchEvent = undefined;
  //   this.clearCanvas();
  // }

  private clearCanvas(canvas: ElementRef = this.selectCanvas) {
    const ctx = canvas.nativeElement.getContext('2d');
    ctx.clearRect(0, 0, canvas.nativeElement.width, this.selectCanvas.nativeElement.height);
  }

  private checkIfWordIsSelected(
    word: WordSearchPuzzleWord,
    start: WordSearchPuzzlePoint = this.selectStart,
    end: WordSearchPuzzlePoint = this.selectEnd
  ) {
    return (
      this.foundWords.map((w) => w.text).indexOf(word.text) === -1 &&
      ((word.start.column === start.column &&
        word.start.row === start.row &&
        word.end.column === end.column &&
        word.end.row === end.row) ||
        (word.start.column === end.column &&
          word.start.row === end.row &&
          word.end.column === start.column &&
          word.end.row === start.row))
    );
  }
}
